/**
 * ADA Fix: Form Field Error Association
 * 
 * Issue: Error messages are not programmatically associated with their 
 * corresponding form fields using aria-describedby, causing screen readers
 * to miss errors when navigating in forms mode.
 * 
 * Solution: Automatically associates error messages with form fields using 
 * aria-describedby and manages aria-invalid state.
 */

(function() {
  'use strict';

  /**
   * Associates an error message with its form field
   */
  function associateErrorWithField(errorElement, fieldId) {
    const field = document.getElementById(fieldId);
    if (!field || !errorElement) return;

    // Ensure error has an ID
    if (!errorElement.id) {
      errorElement.id = fieldId + '-error';
    }

    // Get current aria-describedby value
    const currentDescribedBy = field.getAttribute('aria-describedby') || '';
    const describedByIds = currentDescribedBy.split(' ').filter(id => id);

    // Add error ID if not already present
    if (!describedByIds.includes(errorElement.id)) {
      describedByIds.push(errorElement.id);
      field.setAttribute('aria-describedby', describedByIds.join(' '));
    }

    // Set aria-invalid when error is visible
    const display = window.getComputedStyle(errorElement).display;
    if (display !== 'none' && errorElement.textContent.trim()) {
      field.setAttribute('aria-invalid', 'true');
    }
  }

  /**
   * Removes error association when error is cleared
   */
  function removeErrorAssociation(errorElement, fieldId) {
    const field = document.getElementById(fieldId);
    if (!field || !errorElement) return;

    // Remove aria-invalid
    field.removeAttribute('aria-invalid');

    // Optionally keep aria-describedby for other purposes
    // but we can remove the error ID if error is hidden
    const display = window.getComputedStyle(errorElement).display;
    if (display === 'none') {
      field.removeAttribute('aria-invalid');
    }
  }

  /**
   * Handle Klaviyo footer newsletter form
   */
  function handleKlaviyoForm() {
    const emailField = document.getElementById('k_id_email');
    const errorMessage = document.getElementById('klaviyo-email-error');
    const successMessage = document.getElementById('klaviyo-email-success');

    if (!emailField) return;

    // Watch for error visibility changes
    if (errorMessage) {
      const errorObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
            const display = window.getComputedStyle(errorMessage).display;
            
            if (display !== 'none' && errorMessage.textContent.trim()) {
              emailField.setAttribute('aria-invalid', 'true');
            } else {
              emailField.removeAttribute('aria-invalid');
            }
          }
          
          // Also watch for content changes
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const display = window.getComputedStyle(errorMessage).display;
            if (display !== 'none' && errorMessage.textContent.trim()) {
              emailField.setAttribute('aria-invalid', 'true');
            }
          }
        });
      });

      errorObserver.observe(errorMessage, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    // Watch for success message to clear error state
    if (successMessage) {
      const successObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          const display = window.getComputedStyle(successMessage).display;
          if (display !== 'none' && successMessage.textContent.trim()) {
            emailField.removeAttribute('aria-invalid');
          }
        });
      });

      successObserver.observe(successMessage, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Handle standard Shopify forms (newsletter, contact, etc.)
   */
  function handleStandardForms() {
    // Find all form fields with associated error messages
    document.querySelectorAll('.form-message').forEach(function(message) {
      // Try to find associated input field
      const messageId = message.id;
      if (!messageId) return;

      // Extract field name from message ID (e.g., "Newsletter-error--123" -> "NewsletterForm--123")
      const match = messageId.match(/(.*)-(?:error|success)--(.+)/);
      if (!match) return;

      const baseName = match[1];
      const sectionId = match[2];
      
      // Try different possible field ID formats
      const possibleFieldIds = [
        baseName + 'Form--' + sectionId,
        baseName + '--' + sectionId,
        baseName + '-' + sectionId,
        'Contact' + baseName + '-' + sectionId,
        'RegisterForm-' + baseName.toLowerCase()
      ];

      for (const fieldId of possibleFieldIds) {
        const field = document.getElementById(fieldId);
        if (field) {
          // Ensure the message has a proper ID
          if (!message.id) {
            message.id = fieldId + '-message';
          }

          // Add aria-describedby to field if not present
          const currentDescribedBy = field.getAttribute('aria-describedby') || '';
          if (!currentDescribedBy.includes(message.id)) {
            const describedByIds = currentDescribedBy ? currentDescribedBy + ' ' + message.id : message.id;
            field.setAttribute('aria-describedby', describedByIds);
          }

          // Set aria-invalid if it's an error message
          if (message.classList.contains('form-message') && 
              message.textContent.toLowerCase().includes('error')) {
            field.setAttribute('aria-invalid', 'true');
          }

          break;
        }
      }
    });
  }

  /**
   * Handle any form field with nearby error message
   */
  function handleGenericFormErrors() {
    // Find all inputs with required attribute
    document.querySelectorAll('input[required], textarea[required], select[required]').forEach(function(field) {
      // Look for nearby error message
      const parent = field.closest('.input-group, .form-group, form');
      if (!parent) return;

      const errorMsg = parent.querySelector('.error_message, .form-message, [class*="error"]');
      if (!errorMsg) return;

      // Create ID for error if it doesn't have one
      if (!errorMsg.id) {
        errorMsg.id = field.id ? field.id + '-error' : 'error-' + Date.now();
      }

      // Add aria-describedby to field
      const currentDescribedBy = field.getAttribute('aria-describedby') || '';
      const describedByIds = currentDescribedBy.split(' ').filter(id => id);
      
      if (!describedByIds.includes(errorMsg.id)) {
        describedByIds.push(errorMsg.id);
        field.setAttribute('aria-describedby', describedByIds.join(' '));
      }

      // Watch for error visibility
      const observer = new MutationObserver(function(mutations) {
        const display = window.getComputedStyle(errorMsg).display;
        if (display !== 'none' && errorMsg.textContent.trim()) {
          field.setAttribute('aria-invalid', 'true');
        } else {
          field.removeAttribute('aria-invalid');
        }
      });

      observer.observe(errorMsg, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true
      });
    });
  }

  /**
   * Initialize all error associations
   */
  function initializeErrorAssociations() {
    handleKlaviyoForm();
    handleStandardForms();
    handleGenericFormErrors();
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeErrorAssociations);
  } else {
    initializeErrorAssociations();
  }

  // Re-run when new forms are added to the page
  const pageObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // If a form was added, re-initialize
            if (node.tagName === 'FORM' || node.querySelector('form')) {
              setTimeout(initializeErrorAssociations, 100);
            }
          }
        });
      }
    });
  });

  pageObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

})();
